import React, { createContext, useState, useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AuthContext } from '../../auth/contexts/AuthContext';
import {
  DeploymentStats,
  MachineEcrDeployment,
  EcrDeployment,
  Machine,
  CreateAssignmentRequest,
} from '../model';
import * as api from '../api/ecrDeploymentsApi';

interface EcrDeploymentsContextType {
  deploymentStats: DeploymentStats | null;
  machineDeployments: MachineEcrDeployment[];
  ecrDeployments: EcrDeployment[];
  machines: Machine[];
  loading: boolean;
  error: string | null;

  fetchDeploymentStats: () => Promise<void>;
  fetchMachineDeployments: (filters?: any) => Promise<void>;
  fetchEcrDeployments: (filters?: any) => Promise<void>;
  fetchMachines: () => Promise<void>;
  createMachineDeployment: (data: CreateAssignmentRequest) => Promise<void>;
  cancelMachineDeployment: (deploymentId: number) => Promise<void>;
  triggerDeployments: () => Promise<any>;
}

export const EcrDeploymentsContext = createContext<EcrDeploymentsContextType>({
  deploymentStats: null,
  machineDeployments: [],
  ecrDeployments: [],
  machines: [],
  loading: false,
  error: null,
  fetchDeploymentStats: async () => {},
  fetchMachineDeployments: async () => {},
  fetchEcrDeployments: async () => {},
  fetchMachines: async () => {},
  createMachineDeployment: async () => {},
  cancelMachineDeployment: async () => {},
  triggerDeployments: async () => ({}),
});

interface Props {
  children: React.ReactNode;
}

export const EcrDeploymentsProvider: React.FC<Props> = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { accessToken } = useContext(AuthContext);

  const [deploymentStats, setDeploymentStats] = useState<DeploymentStats | null>(null);
  const [machineDeployments, setMachineDeployments] = useState<MachineEcrDeployment[]>([]);
  const [ecrDeployments, setEcrDeployments] = useState<EcrDeployment[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getToken = async (): Promise<string> => {
    if (accessToken) return accessToken;
    return await getAccessTokenSilently();
  };

  const fetchDeploymentStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const stats = await api.getDeploymentStats(token);
      setDeploymentStats(stats);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching deployment stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMachineDeployments = async (filters?: any) => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await api.getMachineDeployments(token, filters);
      setMachineDeployments(response.results);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching machine deployments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEcrDeployments = async (filters?: any) => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await api.getEcrDeployments(token, filters);
      setEcrDeployments(response.results);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching ECR deployments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMachines = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const machinesData = await api.getMachines(token);
      setMachines(machinesData);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching machines:', err);
    } finally {
      setLoading(false);
    }
  };

  const createMachineDeployment = async (data: CreateAssignmentRequest) => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      await api.createMachineDeployment(token, data);
      // Refresh the list
      await fetchMachineDeployments();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const cancelMachineDeployment = async (deploymentId: number) => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      await api.cancelMachineDeployment(token, deploymentId);
      // Refresh the list
      await fetchMachineDeployments();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const triggerDeployments = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const result = await api.triggerDeployments(token);
      return result;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <EcrDeploymentsContext.Provider
      value={{
        deploymentStats,
        machineDeployments,
        ecrDeployments,
        machines,
        loading,
        error,
        fetchDeploymentStats,
        fetchMachineDeployments,
        fetchEcrDeployments,
        fetchMachines,
        createMachineDeployment,
        cancelMachineDeployment,
        triggerDeployments,
      }}
    >
      {children}
    </EcrDeploymentsContext.Provider>
  );
};
